import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import type { VacancyReportCharts as VacancyReportChartsData, VacancyChartPoint } from '../services/vacancyReportService';

ChartJS.register(ArcElement, Tooltip, Legend);

// Power BI's default report theme palette (CY24SU06) - the exact colors BI's
// own Vacancy Report pies use. Extracted from the report's theme file so our
// charts render with the same colors as the BI report, cycling for the
// hundreds of by-store slices exactly as BI does.
const POWER_BI_PALETTE = [
  '#118DFF', '#12239E', '#E66C37', '#6B007B', '#E044A7', '#744EC2', '#D9B300',
  '#D64550', '#197278', '#1AAB40', '#15C6F4', '#4092FF', '#FFA058', '#BE5DC9',
  '#F472D0', '#B5A1FF', '#C4A200', '#FF8080', '#00DBBC', '#5BD667', '#0091D5',
  '#4668C5', '#FF6300', '#99008A', '#EC008C', '#533285', '#99700A', '#BA141A',
  '#1F9A85', '#25891C', '#0057A2', '#002050', '#C94F0F', '#450F54', '#B60064',
  '#34124F', '#6A5A29', '#0C3D37', '#0B511F',
];

const isDarkMode = () => document.documentElement.classList.contains('dark');

const getChartTheme = () => {
  const dark = isDarkMode();
  return {
    textColor: dark ? 'rgba(203, 213, 225, 0.9)' : 'rgba(51, 65, 85, 0.9)',
    tooltipBg: dark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    tooltipText: dark ? '#e2e8f0' : '#334155',
    tooltipBorder: dark ? 'rgba(51, 65, 85, 0.5)' : 'rgba(203, 213, 225, 0.5)',
  };
};

// Assigns one palette color per distinct label, in first-seen (highest-value)
// order, reused for every slice sharing that label. Needed because the
// by-store Ageing charts can contain multiple slices for the same store (BI
// groups by store + exact ageing-days value, not store alone), so repeated
// labels must keep the same color to read as "one store".
const buildColorMap = (points: VacancyChartPoint[]): Map<string, string> => {
  const map = new Map<string, string>();
  points.forEach((point) => {
    if (!map.has(point.label)) {
      map.set(point.label, POWER_BI_PALETTE[map.size % POWER_BI_PALETTE.length]);
    }
  });
  return map;
};

interface LegendEntry {
  label: string;
  value: number;
  color: string;
}

interface VacancyPieCardProps {
  title: string;
  legendTitle: string;
  points: VacancyChartPoint[];
  dense?: boolean;
}

const VacancyPieCard: React.FC<VacancyPieCardProps> = ({ title, legendTitle, points, dense }) => {
  const theme = getChartTheme();

  const colorByLabel = useMemo(() => buildColorMap(points || []), [points]);

  // One legend row per distinct label, values aggregated (matters for the
  // exploded ageing charts where a store spans several slices), sorted by
  // total descending so the biggest contributors sit at the top of the
  // scroll list - same ordering BI's legend uses.
  const legendEntries = useMemo<LegendEntry[]>(() => {
    const totals = new Map<string, number>();
    (points || []).forEach((point) => {
      totals.set(point.label, (totals.get(point.label) ?? 0) + point.value);
    });
    return Array.from(totals.entries())
      .map(([label, value]) => ({ label, value, color: colorByLabel.get(label) as string }))
      .sort((a, b) => b.value - a.value);
  }, [points, colorByLabel]);

  if (!points || points.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <h4 className="mb-3 text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h4>
        <div className="flex h-64 items-center justify-center text-sm text-slate-400 dark:text-slate-500">
          No data available
        </div>
      </div>
    );
  }

  const chartData = {
    labels: points.map((point) => point.label),
    datasets: [
      {
        data: points.map((point) => point.value),
        backgroundColor: points.map((point) => colorByLabel.get(point.label)),
        // Dense (by-store) charts pack hundreds of sub-degree wedges edge to
        // edge - any per-wedge stroke is wide relative to the wedge and
        // washes the pie toward grey. Province charts (~9 slices) keep a thin
        // separator, which reads cleanly there.
        borderColor: dense ? undefined : isDarkMode() ? 'rgba(30, 41, 59, 0.9)' : '#ffffff',
        borderWidth: dense ? 0 : 1.5,
        hoverOffset: dense ? 0 : 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // More backing pixels per degree keeps thin dense-chart wedges crisp
    // rather than anti-aliasing into a blur near the centre.
    devicePixelRatio: dense ? Math.max(3, window.devicePixelRatio || 1) : window.devicePixelRatio || 1,
    animation: dense ? (false as const) : undefined,
    plugins: {
      // Built-in legend disabled - we render our own scrollable panel so all
      // 400+ entries are reachable (BI's default legend scrolls the same way).
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.tooltipBg,
        titleColor: theme.tooltipText,
        bodyColor: theme.tooltipText,
        borderColor: theme.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          // Match BI's tooltip: "<value> (<pct>%)" where pct is the slice's
          // share of the whole pie's total - e.g. "4 (2.2%)".
          label: (context: {
            label?: string;
            parsed: number;
            dataset?: { data?: number[] };
          }) => {
            const value = context.parsed;
            const values = context.dataset?.data ?? [];
            const total = values.reduce((sum, current) => sum + (Number(current) || 0), 0);
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${context.label ?? ''}: ${value} (${percent}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <h4 className="mb-3 text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h4>
      <div className="flex items-stretch gap-3">
        <div className="relative h-64 min-w-0 flex-1">
          <Pie data={chartData} options={options} />
        </div>
        <div className="flex w-32 shrink-0 flex-col">
          <div className="mb-1 text-xs font-bold text-slate-600 dark:text-slate-300">{legendTitle}</div>
          <div className="max-h-56 overflow-y-auto pr-1">
            {legendEntries.map((entry) => (
              <div
                key={entry.label}
                className="flex items-center gap-1.5 py-0.5 text-[11px] text-slate-600 dark:text-slate-300"
                title={`${entry.label}: ${entry.value}`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="truncate">{entry.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface VacancyReportChartGridProps {
  charts: VacancyReportChartsData | undefined;
  isLoading: boolean;
  countByStoreTitle: string;
  countByProvinceTitle: string;
}

const ChartLoadingCard: React.FC = () => (
  <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
    <div className="mb-3 h-4 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
    <div className="h-64 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
  </div>
);

const VacancyReportChartGrid: React.FC<VacancyReportChartGridProps> = ({
  charts,
  isLoading,
  countByStoreTitle,
  countByProvinceTitle,
}) => {
  if (isLoading) {
    return (
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ChartLoadingCard />
        <ChartLoadingCard />
        <ChartLoadingCard />
        <ChartLoadingCard />
      </div>
    );
  }

  if (!charts) {
    return null;
  }

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
      <VacancyPieCard title={countByStoreTitle} legendTitle="Store Code" points={charts.open_by_store} dense />
      <VacancyPieCard title={countByProvinceTitle} legendTitle="Province" points={charts.open_by_province} />
      <VacancyPieCard title="Ageing by Store" legendTitle="Store Code" points={charts.ageing_by_store} dense />
      <VacancyPieCard title="Ageing by Province" legendTitle="Province" points={charts.ageing_by_province} />
    </div>
  );
};

export default VacancyReportChartGrid;
